import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { FormBuilder, FieldControl, Validators } from 'react-reactive-form';
import { closePopupIcon } from '../assets/superAdminAssets';
import { getNormalizedValues, markAllControlsDirtyAndTouched, printInvalidControls } from '../helpers/form.helper';
import { useAppContext } from '../contexts/AppContext';
import { Constants, limitDurationOptions, unlimitedLimitedOptions } from '../constants';
import { getSubscriptionPlanDetails, insertUpdateSubscriptionPlan } from '../services/subscriptionPlan.service';
import AmountContainer from './AmountContainer';
import { positiveNumberValidator } from '../helpers/validator.helper';
import {
  trialPeriodDaysValidator,
  borrowersUnlimitedValidator,
  partnersUnlimitedValidator,
  usersUnlimitedValidator,
  transactionsUnlimitedValidator,
  creditReportUnlimitedValidator,
  thirdPartyReportsUnlimitedValidator,
  helloSignUnlimitedValidator,
  creditReportStatusValidator,
  thirdPartyReportsStatusValidator,
  helloSignStatusValidator,
} from '../helpers/subscriptionPlanValidators';
import { getNumber } from '../helpers/number.helper';

const initialFormValues = {
  id: 0,
  planName: '',
  price: '',
  allowTrialPeriod: '0',
  trialPeriodDays: '',
  creditReport: '',
  creditReportStatus: '0',
  creditReportLimit: '',
  creditReportOverCharge: '',
  thirdPartyReports: '',
  thirdPartyReportsStatus: '0',
  thirdPartyReportsLimit: '',
  thirdPartyReportsOverCharge: '',
  helloSign: '',
  helloSignStatus: '0',
  helloSignLimit: '',
  helloSignOverCharge: '',
  noofBorrowers: '',
  noofBorrowersLimit: '',
  borrowersOverCharge: '',
  noofPartners: '',
  noofPartnersLimit: '',
  partnersOverCharge: '',
  limitDuration: '1',
  apiName: 'microbit',
  noofUsers: '',
  noofUsersLimit: '',
  usersOverCharge: '',
  noofTransactions: '',
  noofTransactionsLimit: '',
  transactionsOverCharge: '',
}

const MAX_PLAN_NAME_LENGTH = 25;

const normalizePlanDataWithDefaults = (data) => {
  data.trialPeriodDays = data.trialPeriodDays === '' ? 0 : data.trialPeriodDays;
  data.noofBorrowersLimit = data.noofBorrowersLimit === '' ? 0 : data.noofBorrowersLimit;
  data.noofPartnersLimit = data.noofPartnersLimit === '' ? 0 : data.noofPartnersLimit;
  data.noofUsersLimit = data.noofUsersLimit === '' ? 0 : data.noofUsersLimit;
  data.noofTransactionsLimit = data.noofTransactionsLimit === '' ? 0 : data.noofTransactionsLimit;
  data.creditReportLimit = data.creditReportLimit === '' ? 0 : data.creditReportLimit;
  data.thirdPartyReportsLimit = data.thirdPartyReportsLimit === '' ? 0 : data.thirdPartyReportsLimit;
  data.helloSignLimit = data.helloSignLimit === '' ? 0 : data.helloSignLimit;
  data.borrowersOverCharge = data.borrowersOverCharge === '' ? 0 : data.borrowersOverCharge;
  data.partnersOverCharge = data.partnersOverCharge === '' ? 0 : data.partnersOverCharge;
  data.usersOverCharge = data.usersOverCharge === '' ? 0 : data.usersOverCharge;
  data.transactionsOverCharge = data.transactionsOverCharge === '' ? 0 : data.transactionsOverCharge;
  data.creditReportOverCharge = data.creditReportOverCharge === '' ? 0 : data.creditReportOverCharge;
  data.thirdPartyReportsOverCharge = data.thirdPartyReportsOverCharge === '' ? 0 : data.thirdPartyReportsOverCharge;
  data.helloSignOverCharge = data.helloSignOverCharge === '' ? 0 : data.helloSignOverCharge;
  data.creditReportStatus = data.creditReportStatus === '' ? 0 : data.creditReportStatus;
  data.thirdPartyReportsStatus = data.thirdPartyReportsStatus === '' ? 0 : data.thirdPartyReportsStatus;
  data.helloSignStatus = data.helloSignStatus === '' ? 0 : data.helloSignStatus;
  data.creditReport = data.creditReport === '' ? 1 : data.creditReport;
  data.thirdPartyReports = data.thirdPartyReports === '' ? 1 : data.thirdPartyReports;
  data.helloSign = data.helloSign === '' ? 1 : data.helloSign;
  return data;
}

const AddPlanDrawer = ({ show, showAnimation, onClose, planId, onPlanAdded }) => {
  const { showLoader, hideLoader, showToaster, hideToaster } = useAppContext();
  const [planNameValue, setPlanNameValue] = useState('');
  const planForm = useMemo(
    () =>
      FormBuilder.group({
        id: [0, Validators.required],
        planName: ['', [Validators.required, Validators.maxLength(MAX_PLAN_NAME_LENGTH)]],
        price: ['', [Validators.required, positiveNumberValidator]],
        allowTrialPeriod: ['0', Validators.required],
        trialPeriodDays: ['', trialPeriodDaysValidator],
        noofUsers: ['', Validators.required],
        noofUsersLimit: ['', [usersUnlimitedValidator]],
        usersOverCharge: ['', [usersUnlimitedValidator]],
        noofTransactions: ['', Validators.required],
        noofTransactionsLimit: ['', [transactionsUnlimitedValidator]],
        transactionsOverCharge: ['', [transactionsUnlimitedValidator]],
        creditReportStatus: ['0', Validators.required],
        creditReport: ['0', creditReportStatusValidator],
        creditReportLimit: ['', [creditReportUnlimitedValidator]],
        creditReportOverCharge: ['', [creditReportUnlimitedValidator]],
        thirdPartyReportsStatus: ['0', Validators.required],
        thirdPartyReports: ['0', thirdPartyReportsStatusValidator],
        thirdPartyReportsLimit: ['', [thirdPartyReportsUnlimitedValidator]],
        thirdPartyReportsOverCharge: ['', [thirdPartyReportsUnlimitedValidator]],
        helloSignStatus: ['0', Validators.required],
        helloSign: ['', helloSignStatusValidator],
        helloSignLimit: ['', [helloSignUnlimitedValidator]],
        helloSignOverCharge: ['', [helloSignUnlimitedValidator]],
        noofBorrowers: ['', Validators.required],
        noofBorrowersLimit: ['', [borrowersUnlimitedValidator]],
        borrowersOverCharge: ['', [borrowersUnlimitedValidator]],
        noofPartners: ['', Validators.required],
        noofPartnersLimit: ['', [partnersUnlimitedValidator]],
        partnersOverCharge: ['', [partnersUnlimitedValidator]],
        limitDuration: ['1', Validators.required],
        apiName: ['microbit', Validators.required],
      }),
    []
  );
  const handleClose = () => {
    planForm.reset(initialFormValues);
    setPlanNameValue('');
    onClose();
  };

  const handlePlanAdd = () => {
    planForm.reset(initialFormValues);
    setPlanNameValue('');
    typeof onPlanAdded === 'function' && onPlanAdded();
  };

  useEffect(() => {
    if (planId > 0) {
      fetchSubscriptionPlanDetails();
    } else {
      planForm.reset(initialFormValues);
    }
  }, [planId]);

  const fetchSubscriptionPlanDetails = async () => {
    try {
      showLoader();
      const res = await getSubscriptionPlanDetails(planId);
      if (res.status === 200) {
        const data = typeof res.resultObject === 'string' && res.resultObject !== '' ? JSON.parse(res.resultObject) : res.resultObject;
        planForm.patchValue(data);
        setPlanNameValue(data.planName);
      } else {
        showToaster(res.message, 'error');
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
        planForm.reset(initialFormValues);
      }
    } catch (error) {
      console.log(error);
      showToaster('Failed to fetch subscription plan details', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  }

  const handleSubmit = async (e) => {
    try {
      showLoader();
      e.preventDefault();
      markAllControlsDirtyAndTouched(planForm);
      if (planForm.invalid) {
        printInvalidControls(planForm);
        return;
      }
      let data = getNormalizedValues(planForm.value);
      data = normalizePlanDataWithDefaults(data);
      const res = await insertUpdateSubscriptionPlan(data);
      if (res.status === 200) {
        showToaster(res.message, 'success');
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
        handlePlanAdd();
      } else {
        showToaster(res.message, 'error');
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
      }
    } catch (error) {
      console.log(error);
      showToaster('Failed to add plan', 'error');
    } finally {
      hideLoader();
    }
  };

  if (!show) return null;

  return (
    <>
      <div
        className={`modal-backdrop fade ${showAnimation ? 'show' : ''}`}
        onClick={handleClose}
        style={{ zIndex: 1040 }}
      ></div>
      <div
        className={`modal fade-right d-block drawer-modal custom-full-width-drawer-size ${showAnimation ? 'show' : ''}`}
        tabIndex={-1}
        role="dialog"
        style={{ zIndex: 1050 }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
      >
        <div className="modal-dialog">
          <form onSubmit={handleSubmit}>
            <div className="modal-content">
              <div className="drawer-head d-flex justify-content-between">
                {planId > 0 ? <h3 className="text-break-all">Edit Plan {planNameValue}</h3> : <h3>Add a new plan</h3>}
                <button
                  type="button"
                  className="close border-none"
                  data-dismiss="modal"
                  aria-label="Close"
                  onClick={handleClose}
                >
                  <span aria-hidden="true">
                    <img src={closePopupIcon} alt="" />
                  </span>
                </button>
              </div>
              <div className="drawer-body">
                <div className="row">
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      render={({ handler, touched, hasError }) => (
                        <div
                          className={`form-group ${touched && hasError('required') ? 'error-field' : ''
                            }`}
                        >
                          <label htmlFor="plan_name">
                            Plan Name<span className="asterisk">*</span>
                          </label>
                          <input
                            {...handler()}
                            type="text"
                            className="form-control"
                            id="plan_name"
                            placeholder="Enter plan name"
                            maxLength={MAX_PLAN_NAME_LENGTH}
                          />
                          {touched && hasError('required') && (
                            <span className="error-message">
                              Plan name is required
                            </span>
                          )}
                          {touched && hasError('maxlength') && (
                            <span className="error-message">
                              Plan name must be less than {MAX_PLAN_NAME_LENGTH} characters
                            </span>
                          )}
                        </div>
                      )}
                      control={planForm.get('planName')}
                    />
                  </div>
                  <div className="col-md-4">
                    <AmountContainer
                      name="Price"
                      label="Price (per month)"
                      placeholder="0.00"
                      maxlength={11}
                      showPlaceholderForZero={false}
                      setMinValidation={true}
                      control={planForm.get('price')}
                      isRequired={true}
                      errorMessage="Price is required"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <div className="row mb-3 pl-1">
                    <div className="col-md-2">
                      <label className="mt-1" htmlFor="allow_trial_period">
                        Trial Period<span className="asterisk">*</span>
                      </label>
                    </div>
                    <div className="col-md-1">
                      <FieldControl
                        strict={false}
                        render={({ value, setValue }) => (
                          <div className="lm-toggle-switch">
                            <input
                              type="checkbox"
                              id="allow_trial_period"
                              checked={value === 1}
                              onChange={(e) => {
                                if (e.target.checked && getNumber(planForm.value?.price || "0") <= 0) {
                                  showToaster('Price should be greater than 0 to enable trial period', 'error');
                                  setTimeout(() => {
                                    hideToaster();
                                  }, Constants.TOASTERHIDETIME);
                                } else {
                                  setValue(e.target.checked ? 1 : 0);
                                  planForm.patchValue({
                                    trialPeriodDays: e.target.checked ? "" : '',
                                  });
                                  planForm.get('trialPeriodDays').updateValueAndValidity();
                                }
                              }}
                            />
                            <label htmlFor="allow_trial_period">
                              Trial Period<span className="asterisk">*</span>
                            </label>
                          </div>
                        )}
                        control={planForm.get('allowTrialPeriod')}
                      />
                    </div>
                    <div className="col-md-4">
                      <FieldControl
                        control={planForm.get('allowTrialPeriod')}
                        render={({ value: allowTrialPeriodValue }) => (
                          <AmountContainer
                            name="trialPeriodDays"
                            hideLabel={true}
                            placeholder="Set trial period in days"
                            maxlength={11}
                            showPlaceholderForZero={true}
                            setMinValidation={true}
                            control={planForm.get('trialPeriodDays')}
                            isRequired={true}
                            disabled={!allowTrialPeriodValue || allowTrialPeriodValue === '0'}
                            errorMessage="Trial period is required"
                            allowDecimal={false}
                            hideIcon
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                <h6 className="lm-card-sub-heading">Set Plan Limits</h6>
                <div className="row mb-3 pl-1">
                  <div className="col-md-3">
                    <label className="mt-1" htmlFor="limit_duration">
                      Limit Duration<span className="asterisk">*</span>
                    </label>
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      strict={false}
                      render={({ value, setValue, touched, hasError }) => {
                        const showError = touched && hasError('required');
                        return (
                          <div className={`form-group ${showError ? 'error-field' : ''}`}>
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              id="limit_duration"
                              options={limitDurationOptions}
                              value={limitDurationOptions.find(
                                (opt) => opt.value?.toString() == value?.toString()
                              )}
                              onChange={(option) =>
                                setValue(option ? option.value : '')
                              }
                            />
                            {showError && (
                              <span className="error-message">
                                Limit duration is required
                              </span>
                            )}
                          </div>
                        )
                      }}
                      control={planForm.get('limitDuration')}
                    />
                  </div>
                </div>
                <div className="row mb-3 pl-1">
                  <div className="col-md-3">
                    <label className="mt-1" htmlFor="no_of_users">
                      Number of Borrowers<span className="asterisk">*</span>
                    </label>
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      strict={false}
                      render={({ value, setValue, touched, hasError }) => {
                        const showError = touched && hasError('required');
                        return (
                          <div className={`form-group ${showError ? 'error-field' : ''}`}>
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              id="no_of_users"
                              options={unlimitedLimitedOptions}
                              value={unlimitedLimitedOptions.find(
                                (opt) => opt.value?.toString() == value?.toString()
                              )}
                              onChange={(option) => {
                                const newValue = option ? option.value : '';
                                setValue(newValue);
                                planForm.patchValue({
                                  noofBorrowersLimit: '',
                                  borrowersOverCharge: '',
                                });
                                planForm.get('noofBorrowersLimit').updateValueAndValidity();
                                planForm.get('borrowersOverCharge').updateValueAndValidity();
                              }}
                            />
                            {showError && (
                              <span className="error-message">
                                Number of borrowers is required
                              </span>
                            )}
                          </div>
                        )
                      }}
                      control={planForm.get('noofBorrowers')}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      control={planForm.get('noofBorrowers')}
                      render={({ value: noofBorrowersValue }) => (
                        <AmountContainer
                          name="noofBorrowersLimit"
                          control={planForm.get('noofBorrowersLimit')}
                          placeholder="Set Usage Limit"
                          disabled={noofBorrowersValue === 0 || noofBorrowersValue === '0'}
                          hideLabel={true}
                          isRequired={true}
                          showPlaceholderForZero={true}
                          maxlength={11}
                          errorMessage="Usage limit is required"
                          allowDecimal={false}
                          hideIcon
                        />
                      )}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      control={planForm.get('noofBorrowers')}
                      render={({ value: noofBorrowersValue }) => (
                        <AmountContainer
                          name="borrowersOverCharge"
                          control={planForm.get('borrowersOverCharge')}
                          placeholder="Set Overage Charge (per user)"
                          disabled={noofBorrowersValue === 0 || noofBorrowersValue === '0'}
                          hideLabel={true}
                          isRequired={true}
                          showPlaceholderForZero={true}
                          maxlength={11}
                          errorMessage="Overage charge is required"
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="row mb-3 pl-1">
                  <div className="col-md-3">
                    <label className="mt-1" htmlFor="no_of_partners">
                      Number of Partners<span className="asterisk">*</span>
                    </label>
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      strict={false}
                      render={({ value, setValue, touched, hasError }) => {
                        const showError = touched && hasError('required');
                        return (
                          <div className={`form-group ${showError ? 'error-field' : ''}`}>
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              id="no_of_partners"
                              options={unlimitedLimitedOptions}
                              value={unlimitedLimitedOptions.find(
                                (opt) => opt.value?.toString() == value?.toString()
                              )}
                              onChange={(option) => {
                                const newValue = option ? option.value : '';
                                setValue(newValue);
                                planForm.patchValue({
                                  noofPartnersLimit: '',
                                  partnersOverCharge: '',
                                });
                                planForm.get('noofPartnersLimit').updateValueAndValidity();
                                planForm.get('partnersOverCharge').updateValueAndValidity();
                              }}
                            />
                            {showError && (
                              <span className="error-message">
                                Number of partners is required
                              </span>
                            )}
                          </div>
                        )
                      }}
                      control={planForm.get('noofPartners')}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      control={planForm.get('noofPartners')}
                      render={({ value: noofPartnersValue }) => (
                        <AmountContainer
                          name="noofPartnersLimit"
                          control={planForm.get('noofPartnersLimit')}
                          placeholder="Set Usage Limit"
                          disabled={noofPartnersValue === 0 || noofPartnersValue === '0'}
                          hideLabel={true}
                          isRequired={true}
                          showPlaceholderForZero={true}
                          maxlength={11}
                          errorMessage="Usage limit is required"
                          allowDecimal={false}
                          hideIcon
                        />
                      )}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      control={planForm.get('noofPartners')}
                      render={({ value: noofPartnersValue }) => (
                        <AmountContainer
                          name="partnersOverCharge"
                          control={planForm.get('partnersOverCharge')}
                          placeholder="Set Overage Charge (per user)"
                          disabled={noofPartnersValue === 0 || noofPartnersValue === '0'}
                          hideLabel={true}
                          isRequired={true}
                          showPlaceholderForZero={true}
                          maxlength={11}
                          errorMessage="Overage charge is required"
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="row mb-3 pl-1">
                  <div className="col-md-3">
                    <label className="mt-1" htmlFor="no_of_other_users">
                      Number of other Users<span className="asterisk">*</span>
                    </label>
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      strict={false}
                      render={({ value, setValue, touched, hasError }) => {
                        const showError = touched && hasError('required');
                        return (
                          <div className={`form-group ${showError ? 'error-field' : ''}`}>
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              id="no_of_other_users"
                              options={unlimitedLimitedOptions}
                              value={unlimitedLimitedOptions.find(
                                (opt) => opt.value?.toString() == value?.toString()
                              )}
                              onChange={(option) => {
                                const newValue = option ? option.value : '';
                                setValue(newValue);
                                planForm.patchValue({
                                  noofUsersLimit: '',
                                  usersOverCharge: '',
                                });
                                planForm.get('noofUsersLimit').updateValueAndValidity();
                                planForm.get('usersOverCharge').updateValueAndValidity();
                              }}
                            />
                            {showError && (
                              <span className="error-message">
                                Number of other users is required
                              </span>
                            )}
                          </div>
                        )
                      }}
                      control={planForm.get('noofUsers')}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      control={planForm.get('noofUsers')}
                      render={({ value: noofUsersValue }) => (
                        <AmountContainer
                          name="noofUsersLimit"
                          control={planForm.get('noofUsersLimit')}
                          placeholder="Set Usage Limit"
                          disabled={noofUsersValue === 0 || noofUsersValue === '0'}
                          hideLabel={true}
                          isRequired={true}
                          showPlaceholderForZero={true}
                          maxlength={11}
                          allowDecimal={false}
                          errorMessage="Usage limit is required"
                          hideIcon
                        />
                      )}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      control={planForm.get('noofUsers')}
                      render={({ value: noofUsersValue }) => (
                        <AmountContainer
                          name="usersOverCharge"
                          control={planForm.get('usersOverCharge')}
                          placeholder="Set Overage Charge (per user)"
                          disabled={noofUsersValue === 0 || noofUsersValue === '0'}
                          hideLabel={true}
                          isRequired={true}
                          showPlaceholderForZero={true}
                          maxlength={11}
                          errorMessage="Overage charge is required"
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="row mb-3 pl-1">
                  <div className="col-md-3">
                    <label className="mt-1" htmlFor="no_of_transactions">
                      Number of Transactions<span className="asterisk">*</span>
                    </label>
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      strict={false}
                      render={({ value, setValue, touched, hasError }) => {
                        const showError = touched && hasError('required');
                        return (
                          <div className={`form-group ${showError ? 'error-field' : ''}`}>
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              id="no_of_transactions"
                              options={unlimitedLimitedOptions}
                              value={unlimitedLimitedOptions.find(
                                (opt) => opt.value?.toString() == value?.toString()
                              )}
                              onChange={(option) => {
                                const newValue = option ? option.value : '';
                                setValue(newValue);
                                planForm.patchValue({
                                  noofTransactionsLimit: '',
                                  transactionsOverCharge: '',
                                });
                                planForm.get('noofTransactionsLimit').updateValueAndValidity();
                                planForm.get('transactionsOverCharge').updateValueAndValidity();
                              }}
                            />
                            {showError && (
                              <span className="error-message">
                                Number of transactions is required
                              </span>
                            )}
                          </div>
                        )
                      }}
                      control={planForm.get('noofTransactions')}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      control={planForm.get('noofTransactions')}
                      render={({ value: noofTransactionsValue }) => (
                        <AmountContainer
                          name="noofTransactionsLimit"
                          control={planForm.get('noofTransactionsLimit')}
                          placeholder="Set Usage Limit"
                          disabled={noofTransactionsValue === 0 || noofTransactionsValue === '0'}
                          hideLabel={true}
                          isRequired={true}
                          showPlaceholderForZero={true}
                          maxlength={11}
                          errorMessage="Usage limit is required"
                          allowDecimal={false}
                          hideIcon
                        />
                      )}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      control={planForm.get('noofTransactions')}
                      render={({ value: noofTransactionsValue }) => (
                        <AmountContainer
                          name="transactionsOverCharge"
                          control={planForm.get('transactionsOverCharge')}
                          placeholder="Set Overage Charge (per transaction)"
                          disabled={noofTransactionsValue === 0 || noofTransactionsValue === '0'}
                          hideLabel={true}
                          isRequired={true}
                          showPlaceholderForZero={true}
                          maxlength={11}
                          errorMessage="Overage charge is required"
                        />
                      )}
                    />
                  </div>
                </div>

                <hr className="lm-divider" />
                <FieldControl
                  strict={false}
                  control={planForm.get('apiName')}
                  render={({ value, setValue, touched, hasError }) => (
                    <div
                      className={`form-group ${touched && hasError('required') ? 'error-field' : ''
                        }`}
                    >
                      <div className="">
                        <div className="form-check form-check-inline p-0">
                          <input
                            type="radio"
                            className="form-check-input "
                            id="microbit"
                            name="third_party_platform"
                            checked={value === 'microbit'}
                            onChange={() => setValue('microbit')}
                          />
                          <label className="form-check-label" htmlFor="microbit">
                            MicroBilt
                          </label>
                        </div>
                        {/* <div className="form-check form-check-inline p-0">
                          <input
                            type="radio"
                            className="form-check-input "
                            id="microbit_2"
                            name="third_party_platform"
                            checked={value === 'microbit_2'}
                            onChange={() => setValue('microbit_2')}
                          />
                          <label className="form-check-label" htmlFor="microbit_2">
                            MicroBilt 2
                          </label>
                        </div>
                        <div className="form-check form-check-inline p-0">
                          <input
                            type="radio"
                            className="form-check-input "
                            id="microbit_3"
                            name="third_party_platform"
                            checked={value === 'microbit_3'}
                            onChange={() => setValue('microbit_3')}
                          />
                          <label className="form-check-label" htmlFor="microbit_3">
                            MicroBilt 3
                          </label>
                        </div>
                        <div className="form-check form-check-inline p-0">
                          <input
                            type="radio"
                            className="form-check-input "
                            id="microbit_4"
                            name="third_party_platform"
                            checked={value === 'microbit_4'}
                            onChange={() => setValue('microbit_4')}
                          />
                          <label className="form-check-label" htmlFor="microbit_4">
                            MicroBilt 4
                          </label>
                        </div> */}
                      </div>
                      {touched && hasError('required') && (
                        <span className="error-message">
                          Please select a third party API
                        </span>
                      )}
                    </div>
                  )}
                />

                {/* Credit Report */}
                <div className="row mb-3 pl-1">
                  <div className="col-md-2">
                    <label className="mt-1">
                      Credit Report<span className="asterisk">*</span>
                    </label>
                  </div>
                  <div className="col-md-1">
                    <FieldControl
                      strict={false}
                      render={({ value, setValue }) => (
                        <div className="lm-toggle-switch">
                          <input
                            type="checkbox"
                            id="credit_report"
                            checked={value === 1}
                            onChange={(e) => {
                              setValue(e.target.checked ? 1 : 0);
                              planForm.patchValue({
                                creditReportLimit: '',
                                creditReportOverCharge: '',
                                creditReport: ''
                              });
                              planForm.get('creditReportLimit').updateValueAndValidity();
                              planForm.get('creditReportOverCharge').updateValueAndValidity();
                              planForm.get('creditReport').updateValueAndValidity();
                            }
                            }
                          />
                          <label htmlFor="credit_report">Credit Report</label>
                        </div>
                      )}
                      control={planForm.get('creditReportStatus')}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      strict={false}
                      render={({ value, setValue, touched, hasError }) => {
                        const enabled = planForm.get('creditReportStatus').value === 1;
                        const showError = touched && hasError('required');
                        return (
                          <div className={`form-group ${showError ? 'error-field' : ''}`}>
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              id="credit_report_usage"
                              options={unlimitedLimitedOptions}
                              isDisabled={!enabled}
                              value={unlimitedLimitedOptions.find(
                                (opt) => opt.value?.toString() == value?.toString()
                              ) || ''}
                              onChange={(option) => {
                                const newValue = option ? option.value : '';
                                setValue(newValue);
                                planForm.patchValue({
                                  creditReportLimit: '',
                                  creditReportOverCharge: '',
                                });
                                planForm.get('creditReportLimit').updateValueAndValidity();
                                planForm.get('creditReportOverCharge').updateValueAndValidity();
                              }}
                            />
                            {showError && (
                              <span className="error-message">
                                Credit report usage limit is required
                              </span>
                            )}
                          </div>
                        );
                      }}
                      control={planForm.get('creditReport')}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      control={planForm.get('creditReportStatus')}
                      render={({ value: creditReportValue }) => (
                        <FieldControl
                          control={planForm.get('creditReport')}
                          render={({ value: creditReportStatusValue }) => (
                            <AmountContainer
                              name="creditReportLimit"
                              control={planForm.get('creditReportLimit')}
                              placeholder="Set Usage Limit"
                              disabled={creditReportValue !== 1 && creditReportValue !== '1' ||
                                creditReportStatusValue === 0 || creditReportStatusValue === '0'}
                              hideLabel={true}
                              isRequired={true}
                              showPlaceholderForZero={true}
                              maxlength={11}
                              errorMessage="Usage limit is required"
                              allowDecimal={false}
                              hideIcon
                            />
                          )}
                        />
                      )}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      control={planForm.get('creditReportStatus')}
                      render={({ value: creditReportValue }) => (
                        <FieldControl
                          control={planForm.get('creditReport')}
                          render={({ value: creditReportStatusValue }) => (
                            <AmountContainer
                              name="creditReportOverCharge"
                              control={planForm.get('creditReportOverCharge')}
                              placeholder="Set Overage Charge (per use)"
                              disabled={creditReportValue !== 1 && creditReportValue !== '1' ||
                                creditReportStatusValue === 0 || creditReportStatusValue === '0'}
                              hideLabel={true}
                              isRequired={true}
                              showPlaceholderForZero={true}
                              maxlength={11}
                              errorMessage="Overage charge is required"
                            />
                          )}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Third Party Reports */}
                <div className="row mb-3 pl-1">
                  <div className="col-md-2">
                    <label className="mt-0">
                      Third Party Reports<span className="asterisk">*</span>
                    </label>
                  </div>
                  <div className="col-md-1">
                    <FieldControl
                      strict={false}
                      render={({ value, setValue }) => (
                        <div className="lm-toggle-switch">
                          <input
                            type="checkbox"
                            id="third_party_reports"
                            checked={value === 1}
                            onChange={(e) => {
                              setValue(e.target.checked ? 1 : 0);
                              planForm.patchValue({
                                thirdPartyReportsLimit: '',
                                thirdPartyReportsOverCharge: '',
                                thirdPartyReports: ''
                              });
                              planForm.get('thirdPartyReportsLimit').updateValueAndValidity();
                              planForm.get('thirdPartyReportsOverCharge').updateValueAndValidity();
                            }
                            }
                          />
                          <label htmlFor="third_party_reports">
                            Third Party Reports
                          </label>
                        </div>
                      )}
                      control={planForm.get('thirdPartyReportsStatus')}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      strict={false}
                      render={({ value, setValue, touched, hasError }) => {
                        const enabled = planForm.get('thirdPartyReportsStatus').value === 1;
                        const showError = touched && hasError('required');
                        return (
                          <div className={`form-group ${showError ? 'error-field' : ''}`}>
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              id="third_party_reports_usage"
                              options={unlimitedLimitedOptions}
                              isDisabled={!enabled}
                              value={unlimitedLimitedOptions.find(
                                (opt) => opt.value?.toString() == value?.toString()
                              ) || ''}
                              onChange={(option) => {
                                const newValue = option ? option.value : '';
                                setValue(newValue);
                                planForm.patchValue({
                                  thirdPartyReportsLimit: '',
                                  thirdPartyReportsOverCharge: '',
                                });
                                planForm.get('thirdPartyReportsLimit').updateValueAndValidity();
                                planForm.get('thirdPartyReportsOverCharge').updateValueAndValidity();
                              }}
                            />
                            {showError && (
                              <span className="error-message">
                                Third party reports usage limit is required
                              </span>
                            )}
                          </div>
                        );
                      }}
                      control={planForm.get('thirdPartyReports')}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      control={planForm.get('thirdPartyReportsStatus')}
                      render={({ value: thirdPartyReportsValue }) => (
                        <FieldControl
                          control={planForm.get('thirdPartyReports')}
                          render={({ value: thirdPartyReportsStatusValue }) => (
                            <AmountContainer
                              name="thirdPartyReportsLimit"
                              control={planForm.get('thirdPartyReportsLimit')}
                              placeholder="Set Usage Limit"
                              disabled={thirdPartyReportsValue !== 1 && thirdPartyReportsValue !== '1' ||
                                thirdPartyReportsStatusValue === 0 || thirdPartyReportsStatusValue === '0'}
                              hideLabel={true}
                              isRequired={true}
                              showPlaceholderForZero={true}
                              maxlength={11}
                              errorMessage="Usage limit is required"
                              allowDecimal={false}
                              hideIcon
                            />
                          )}
                        />
                      )}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      control={planForm.get('thirdPartyReportsStatus')}
                      render={({ value: thirdPartyReportsValue }) => (
                        <FieldControl
                          control={planForm.get('thirdPartyReports')}
                          render={({ value: thirdPartyReportsStatusValue }) => (
                            <AmountContainer
                              name="thirdPartyReportsOverCharge"
                              control={planForm.get('thirdPartyReportsOverCharge')}
                              placeholder="Set Overage Charge (per use)"
                              disabled={thirdPartyReportsValue !== 1 && thirdPartyReportsValue !== '1' ||
                                thirdPartyReportsStatusValue === 0 || thirdPartyReportsStatusValue === '0'}
                              hideLabel={true}
                              isRequired={true}
                              showPlaceholderForZero={true}
                              maxlength={11}
                              errorMessage="Overage charge is required"
                            />
                          )}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* HelloSign */}
                <div className="row pl-1">
                  <div className="col-md-2">
                    <h6 className="lm-card-sub-heading mt-1">
                      HelloSign<span className="asterisk">*</span>
                    </h6>
                  </div>
                  <div className="col-md-1">
                    <FieldControl
                      strict={false}
                      render={({ value, setValue }) => (
                        <div className="lm-toggle-switch">
                          <input
                            type="checkbox"
                            id="HelloSign"
                            checked={value === 1}
                            onChange={(e) => {
                              setValue(e.target.checked ? 1 : 0);
                              planForm.patchValue({
                                helloSignLimit: '',
                                helloSignOverCharge: '',
                                helloSign: ''
                              });
                              planForm.get('helloSignLimit').updateValueAndValidity();
                              planForm.get('helloSignOverCharge').updateValueAndValidity();
                              planForm.get('helloSign').updateValueAndValidity();
                            }
                            }
                          />
                          <label htmlFor="HelloSign">HelloSign</label>
                        </div>
                      )}
                      control={planForm.get('helloSignStatus')}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      strict={false}
                      render={({ value, setValue, touched, hasError }) => {
                        const enabled = planForm.get('helloSignStatus').value === 1;
                        const showError = touched && hasError('required');
                        return (
                          <div className={`form-group ${showError ? 'error-field' : ''}`} id="hellosign_usage_container">
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              id="hellosign_usage"
                              options={unlimitedLimitedOptions}
                              isDisabled={!enabled}
                              value={unlimitedLimitedOptions.find(
                                (opt) => opt.value?.toString() == value?.toString()
                              ) || ''}
                              onChange={(option) => {
                                const newValue = option ? option.value : '';
                                setValue(newValue);
                                planForm.patchValue({
                                  helloSignLimit: '',
                                  helloSignOverCharge: '',
                                });
                                planForm.get('helloSignLimit').updateValueAndValidity();
                                planForm.get('helloSignOverCharge').updateValueAndValidity();
                              }}
                            />
                            {showError && (
                              <span className="error-message">
                                HelloSign usage limit is required
                              </span>
                            )}
                          </div>
                        );
                      }}
                      control={planForm.get('helloSign')}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      control={planForm.get('helloSignStatus')}
                      render={({ value: helloSignValue }) => (
                        <FieldControl
                          control={planForm.get('helloSign')}
                          render={({ value: helloSignStatusValue }) => (
                            <AmountContainer
                              name="helloSignLimit"
                              control={planForm.get('helloSignLimit')}
                              placeholder="Set Usage Limit"
                              disabled={helloSignValue !== 1 && helloSignValue !== '1' ||
                                helloSignStatusValue === 0 || helloSignStatusValue === '0'}
                              hideLabel={true}
                              isRequired={true}
                              showPlaceholderForZero={true}
                              maxlength={11}
                              errorMessage="Usage limit is required"
                              allowDecimal={false}
                              hideIcon
                            />
                          )}
                        />
                      )}
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldControl
                      control={planForm.get('helloSignStatus')}
                      render={({ value: helloSignValue }) => (
                        <FieldControl
                          control={planForm.get('helloSign')}
                          render={({ value: helloSignStatusValue }) => (
                            <AmountContainer
                              name="helloSignOverCharge"
                              control={planForm.get('helloSignOverCharge')}
                              placeholder="Set Overage Charge (per use)"
                              disabled={helloSignValue !== 1 && helloSignValue !== '1' ||
                                helloSignStatusValue === 0 || helloSignStatusValue === '0'}
                              hideLabel={true}
                              isRequired={true}
                              showPlaceholderForZero={true}
                              maxlength={11}
                              errorMessage="Overage charge is required"
                            />
                          )}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="sticky-btn-footer d-flex justify-content-end w-100 gap-15">
                  <button type="button" className="lm-button2" onClick={handleClose}>Cancel</button>
                  <button type="submit" className="lm-button1 ml-auto">
                    Save
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddPlanDrawer;

